// Arcade sound kit: synthesised retro effects (no files) plus decoded samples
// for the few sounds that need a recording. One AudioContext, created on the
// first gesture. The on/off switch is shared by every cabinet.

const KEY = "cf_arcade_sound";

let ctx: AudioContext | null = null;
let enabled = (() => {
  try {
    return localStorage.getItem(KEY) !== "off";
  } catch {
    return true;
  }
})();
const subs = new Set<() => void>();

export function arcadeContext(): AudioContext | null {
  if (!enabled) return null;
  if (!ctx) {
    const C = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!C) return null;
    ctx = new C();
  }
  if (ctx.state === "suspended") void ctx.resume();
  return ctx;
}

function tone(type: OscillatorType, f0: number, f1: number, dur: number, vol = 0.18, delay = 0) {
  const a = arcadeContext();
  if (!a) return;
  const t = a.currentTime + delay;
  const o = a.createOscillator();
  const g = a.createGain();
  o.type = type;
  o.frequency.setValueAtTime(f0, t);
  o.frequency.exponentialRampToValueAtTime(Math.max(30, f1), t + dur);
  g.gain.setValueAtTime(vol, t);
  g.gain.exponentialRampToValueAtTime(0.0008, t + dur);
  o.connect(g).connect(a.destination);
  o.start(t);
  o.stop(t + dur + 0.02);
}

function noise(dur: number, vol: number, filter: BiquadFilterType = "highpass", freq = 800, delay = 0) {
  const a = arcadeContext();
  if (!a) return;
  const len = Math.max(1, Math.floor(a.sampleRate * dur));
  const buf = a.createBuffer(1, len, a.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / len) ** 2;
  const src = a.createBufferSource();
  src.buffer = buf;
  const f = a.createBiquadFilter();
  f.type = filter;
  f.frequency.value = freq;
  const g = a.createGain();
  g.gain.value = vol;
  src.connect(f).connect(g).connect(a.destination);
  src.start(a.currentTime + delay);
}

// ---------------------------------------------------------------- samples

const buffers = new Map<string, AudioBuffer | null>();
const loading = new Map<string, Promise<AudioBuffer | null>>();

/** Fetch + decode a sample once. Resolves null when unavailable. */
export function loadSample(url: string): Promise<AudioBuffer | null> {
  if (buffers.has(url)) return Promise.resolve(buffers.get(url) ?? null);
  const pending = loading.get(url);
  if (pending) return pending;
  const p = (async () => {
    try {
      const a = arcadeContext();
      if (!a) return null;
      const res = await fetch(url);
      if (!res.ok) throw new Error(String(res.status));
      const buf = await a.decodeAudioData(await res.arrayBuffer());
      buffers.set(url, buf);
      return buf;
    } catch {
      buffers.set(url, null);
      return null;
    } finally {
      loading.delete(url);
    }
  })();
  loading.set(url, p);
  return p;
}

/** Play a decoded sample; returns false (so callers can fall back) when not ready. */
export function playSample(url: string | undefined, vol = 0.8, rate = 1): boolean {
  if (!url) return false;
  const a = arcadeContext();
  const buf = buffers.get(url);
  if (!a || !buf) {
    if (a && !buffers.has(url)) void loadSample(url);
    return false;
  }
  const src = a.createBufferSource();
  src.buffer = buf;
  src.playbackRate.value = rate;
  const g = a.createGain();
  g.gain.value = vol;
  src.connect(g).connect(a.destination);
  src.start();
  return true;
}

export const arcadeSfx = {
  isEnabled: () => enabled,
  setEnabled(on: boolean) {
    enabled = on;
    try {
      localStorage.setItem(KEY, on ? "on" : "off");
    } catch {
      /* ignore */
    }
    subs.forEach((s) => s());
  },
  subscribe(cb: () => void) {
    subs.add(cb);
    return () => {
      subs.delete(cb);
    };
  },
  unlock() {
    arcadeContext();
  },
  coin() {
    tone("square", 988, 988, 0.07, 0.1);
    tone("square", 1319, 1319, 0.24, 0.1, 0.07);
  },
  start() {
    [523, 659, 784, 1047].forEach((f, i) => tone("square", f, f, 0.1, 0.09, i * 0.09));
  },
  select() {
    tone("square", 880, 880, 0.04, 0.06);
  },
  correct() {
    tone("square", 660, 990, 0.08, 0.12);
    tone("square", 990, 1320, 0.12, 0.1, 0.08);
  },
  wrong() {
    tone("sawtooth", 300, 140, 0.32, 0.1);
    tone("square", 150, 90, 0.3, 0.06, 0.05);
  },
  hint() {
    tone("triangle", 1046, 784, 0.18, 0.08);
  },
  laser() {
    tone("square", 1400, 300, 0.12, 0.07);
  },
  explode() {
    noise(0.32, 0.32, "lowpass", 1400);
    tone("sawtooth", 160, 50, 0.3, 0.07);
  },
  warp() {
    tone("sine", 300, 1800, 0.35, 0.08);
  },
  shieldHit() {
    noise(0.25, 0.3, "bandpass", 500);
    tone("square", 220, 70, 0.35, 0.09);
  },
  combo() {
    [784, 988, 1175, 1568].forEach((f, i) => tone("triangle", f, f * 1.01, 0.08, 0.08, i * 0.05));
  },
  fanfare() {
    [523, 659, 784, 659, 784, 1047].forEach((f, i) => tone("square", f, f, 0.14, 0.08, i * 0.13));
  },
  gameOver() {
    [392, 330, 262, 196].forEach((f, i) => tone("square", f, f * 0.98, 0.22, 0.09, i * 0.22));
  },
  /** Support holds: a bright metal ping. */
  ping() {
    tone("sine", 1760, 1740, 0.6, 0.12);
    tone("sine", 2637, 2600, 0.4, 0.05);
  },
  /** Support strains: a low wooden creak. */
  creak() {
    const a = arcadeContext();
    if (!a) return;
    const t = a.currentTime;
    const o = a.createOscillator();
    const g = a.createGain();
    const lfo = a.createOscillator();
    const lg = a.createGain();
    o.type = "sawtooth";
    o.frequency.setValueAtTime(95, t);
    o.frequency.linearRampToValueAtTime(70, t + 0.7);
    lfo.frequency.value = 22;
    lg.gain.value = 18;
    lfo.connect(lg).connect(o.frequency);
    const f = a.createBiquadFilter();
    f.type = "bandpass";
    f.frequency.value = 420;
    f.Q.value = 3;
    g.gain.setValueAtTime(0.0001, t);
    g.gain.linearRampToValueAtTime(0.16, t + 0.12);
    g.gain.exponentialRampToValueAtTime(0.0008, t + 0.75);
    o.connect(f).connect(g).connect(a.destination);
    o.start(t);
    lfo.start(t);
    o.stop(t + 0.8);
    lfo.stop(t + 0.8);
  },
  /** Support breaks: a sharp snap. */
  snap() {
    noise(0.06, 0.6, "highpass", 1800);
    noise(0.22, 0.25, "lowpass", 700, 0.03);
    tone("square", 320, 80, 0.12, 0.06);
  },
  splash() {
    noise(0.7, 0.3, "lowpass", 900);
  },
};
